import ErrorOutlinedIcon from "@mui/icons-material/ErrorOutlined";
import HistoryIcon from "@mui/icons-material/History";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { alpha, styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { type MouseEvent, useEffect, useMemo, useRef, useState } from "react";
import ProposalIssues, { ProposalIssueLevel } from "work/proposals/issues/proposal-issues";
import {
  proposalUnreadChangeFields,
  type ProposalUnreadEnvelope,
} from "work/proposals/negotiation/proposal-field-unread";
import { ProposalFieldCategoryType } from "work/proposals/proposal-fields/proposal-field";

const TabLabelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-end",
  boxSizing: "border-box",
  flexWrap: "nowrap",
  gap: "6px",
  marginLeft: theme.spacing(-2),
  paddingLeft: theme.spacing(2),
  whiteSpace: "nowrap",
  "&:hover .status-chip-label-text": {
    marginLeft: theme.spacing(0.375),
    maxWidth: theme.spacing(15),
    opacity: 1,
  },
}));

const BadgeContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "flex-start",
  gap: "0px",
  flexDirection: "row",
  flexWrap: "nowrap",
  "& > .MuiChip-root": {
    border: 0,
  },
}));

const StatusChip = styled(Chip)(({ theme }) => ({
  height: theme.spacing(2.5),
  fontSize: "0.65rem",
  fontWeight: 600,
  overflow: "hidden",
  transition: theme.transitions.create("max-width", {
    duration: theme.transitions.duration.shortest,
  }),
  "& .MuiChip-icon": {
    fontSize: "0.85rem",
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(-0.25),
  },
  "& .MuiChip-label": {
    paddingLeft: theme.spacing(0.75),
    paddingRight: theme.spacing(0.75),
  },
}));

const StatusChipLabel = styled("span")(() => ({
  alignItems: "center",
  display: "inline-flex",
  whiteSpace: "nowrap",
}));

const StatusChipLabelText = styled("span")(({ theme }) => ({
  display: "inline-block",
  marginLeft: 0,
  maxWidth: 0,
  opacity: 0,
  overflow: "hidden",
  transition: theme.transitions.create(["margin-left", "max-width", "opacity"], {
    duration: theme.transitions.duration.shortest,
  }),
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  maxWidth: theme.spacing(42),
  padding: theme.spacing(1.25),
}));

const PopoverSection = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
}));

const PopoverMessage = styled(Typography)(() => ({
  fontSize: "0.75rem",
  lineHeight: 1.35,
}));

const StatusPopper = styled(Popper)(({ theme }) => ({
  pointerEvents: "none",
  zIndex: theme.zIndex.tooltip,
}));

const PopperSurface = styled(Paper)(({ theme }) => ({
  backdropFilter: "blur(10px) brightness(105%)",
  backgroundColor: alpha(theme.palette.background.paper, 0.5),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: "none",
  pointerEvents: "none",
}));

/**
 * What a proposal tab is advertising: its blocking issues, its warnings, the counterparty changes the
 * viewer has not looked at, and the changes the viewer has staged but not sent.
 *
 * Comments are deliberately absent. They used to be counted here per category, back when a proposal
 * carried a comment thread per field; it now has one conversation for the whole negotiation, so an
 * unread comment belongs to no tab in particular and the same count on all six tabs would only claim a
 * division that no longer exists. The Comments/Changes pane and its button carry that signal.
 */
type ProposalIssuesBadgeProps = {
  issues?: ProposalIssues;
  category: ProposalFieldCategoryType;
  title?: string;
  pendingChangeCount?: number;
  /**
   * The negotiation collaboration envelope, used to derive this tab's unviewed-change count the same way
   * `commentThreads`/`issues` are used to derive comment/issue counts — no separate per-tab plumbing.
   * Omitted (e.g. before the negotiation stream exists) simply shows no unviewed-change chip.
   */
  envelope?: ProposalUnreadEnvelope;
  /** Required alongside `envelope` — the unviewed-change count never counts the viewer's own edits. */
  currentUserId?: string;
};

export default function ProposalIssuesBadge(props: Readonly<ProposalIssuesBadgeProps>) {
  const { issues, category, title, envelope, currentUserId } = props;
  const pendingChangeCount = props.pendingChangeCount ?? 0;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closePopoverTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const badgeSummary = useMemo(() => {
    const filteredIssues = issues?.entries.filter((issue) => issue.field.category === category) ?? [];
    const criticalIssues = filteredIssues.filter((issue) => issue.level === ProposalIssueLevel.Critical);
    const warningIssues = filteredIssues.filter((issue) => issue.level === ProposalIssueLevel.Warning);
    const unviewedChangeFields = envelope ? proposalUnreadChangeFields(envelope, currentUserId) : [];
    const numUnviewedChanges = unviewedChangeFields.filter((field) => field.category === category).length;

    return {
      criticalIssues,
      warningIssues,
      numErrors: criticalIssues.length,
      numWarnings: warningIssues.length,
      numUnviewedChanges,
    };
  }, [category, issues, envelope, currentUserId]);

  const popoverContent = useMemo(() => {
    if (
      badgeSummary.criticalIssues.length === 0 &&
      badgeSummary.warningIssues.length === 0 &&
      badgeSummary.numUnviewedChanges === 0 &&
      pendingChangeCount === 0
    ) {
      return "";
    }

    return (
      <PopoverContent>
        {badgeSummary.criticalIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Blockers</Typography>
            {badgeSummary.criticalIssues.map((issue) => (
              <PopoverMessage key={issue.field.name + (issue.field.id?.value ?? "")}>
                {issue.description}
              </PopoverMessage>
            ))}
          </PopoverSection>
        )}
        {badgeSummary.warningIssues.length > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Warnings</Typography>
            {badgeSummary.warningIssues.map((issue) => (
              <PopoverMessage key={issue.field.name + (issue.field.id?.value ?? "")}>
                {issue.description}
              </PopoverMessage>
            ))}
          </PopoverSection>
        )}
        {pendingChangeCount > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Pending</Typography>
            <PopoverMessage key={`pending-change-${category}`}>
              {pendingChangeCount} pending change{pendingChangeCount > 1 && "s"} not yet shared.
            </PopoverMessage>
          </PopoverSection>
        )}
        {badgeSummary.numUnviewedChanges > 0 && (
          <PopoverSection>
            <Typography variant="subtitle2">Changes</Typography>
            <PopoverMessage key={`unviewed-change-${category}`}>
              {badgeSummary.numUnviewedChanges} unviewed change{badgeSummary.numUnviewedChanges > 1 ? "s" : ""}.
            </PopoverMessage>
          </PopoverSection>
        )}
      </PopoverContent>
    );
  }, [badgeSummary, category, pendingChangeCount]);
  const isPopoverOpen = Boolean(anchorEl) && Boolean(popoverContent);
  const popperOffset = anchorEl?.closest(".Mui-selected") ? 16 : 8;

  useEffect(
    () => () => {
      if (closePopoverTimeout.current) clearTimeout(closePopoverTimeout.current);
    },
    [],
  );

  function clearClosePopoverTimeout() {
    if (!closePopoverTimeout.current) return;

    clearTimeout(closePopoverTimeout.current);
    closePopoverTimeout.current = null;
  }

  function openPopover(event: MouseEvent<HTMLElement>) {
    if (!popoverContent) return;

    clearClosePopoverTimeout();
    setAnchorEl(event.currentTarget);
  }

  function closePopover() {
    clearClosePopoverTimeout();
    closePopoverTimeout.current = setTimeout(() => {
      setAnchorEl(null);
    }, 100);
  }

  return (
    <>
      <TabLabelContainer
        onMouseEnter={openPopover}
        onMouseLeave={closePopover}
      >
        <span>{title ?? category.toString()}</span>
        <BadgeContainer>
          {badgeSummary.numErrors > 0 && (
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numErrors, "blocker")}
              color="error"
              data-testid="issue-critical-badge"
              icon={<ErrorOutlinedIcon />}
              label={getStatusChipLabel(badgeSummary.numErrors, "blocker")}
              size="small"
              variant="outlined"
            />
          )}
          {badgeSummary.numWarnings > 0 && (
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numWarnings, "warning")}
              color="warning"
              data-testid="issue-warning-badge"
              icon={<WarningAmberIcon />}
              label={getStatusChipLabel(badgeSummary.numWarnings, "warning")}
              size="small"
              variant="outlined"
            />
          )}
          {pendingChangeCount > 0 && (
            <StatusChip
              aria-label={getStatusLabel(pendingChangeCount, "pending change")}
              color="info"
              data-testid="pending-change-badge"
              icon={<HistoryIcon />}
              label={getStatusChipLabel(pendingChangeCount, "pending change")}
              size="small"
              variant="outlined"
            />
          )}
          {badgeSummary.numUnviewedChanges > 0 && (
            // Secondary (purple), never error — an unviewed change is something to notice, not an alarm.
            <StatusChip
              aria-label={getStatusLabel(badgeSummary.numUnviewedChanges, "unviewed change")}
              color="secondary"
              data-testid="unviewed-change-badge"
              icon={<PublishedWithChangesIcon />}
              label={getStatusChipLabel(badgeSummary.numUnviewedChanges, "unviewed change")}
              size="small"
              variant="outlined"
            />
          )}
        </BadgeContainer>
      </TabLabelContainer>
      <StatusPopper
        anchorEl={anchorEl}
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, popperOffset],
            },
          },
        ]}
        open={isPopoverOpen}
        placement="bottom-start"
      >
        <PopperSurface elevation={0}>{popoverContent}</PopperSurface>
      </StatusPopper>
    </>
  );
}

function getStatusLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return `${count} ${count === 1 ? singularLabel : pluralLabel}`;
}

function getStatusChipLabel(count: number, singularLabel: string, pluralLabel = `${singularLabel}s`) {
  return (
    <StatusChipLabel>
      <span>{count}</span>
      <StatusChipLabelText className="status-chip-label-text">
        {count === 1 ? singularLabel : pluralLabel}
      </StatusChipLabelText>
    </StatusChipLabel>
  );
}
